import React from 'react';
import { twMerge } from 'tailwind-merge';

import type { IconProps } from '../icon/icon';
import Icon from '../icon/icon';
import Typography from '../typography/typography';

type ButtonProps = {
  className?: string;
  leftIconProps?: IconProps;
  rightIconProps?: IconProps;
  label: string;
  labelClassName?: string;
  variant: 'primary' | 'outlined' | 'danger' | 'transparent';
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  loading?: boolean;
};

const Button: React.FC<ButtonProps> = (props) => {
  const {
    className,
    labelClassName,
    label,
    loading,
    onClick,
    rightIconProps,
    leftIconProps,
    ...other
  } = props;
  const buttonClassName = twMerge(
    'flex flex-row items-center h-10 px-4 rounded-md ',
    'transition ease-in-out duration-150',
    'disabled:cursor-not-allowed',
    loading && 'cursor-not-allowed',
    props.variant === 'primary' && 'bg-primary disabled:bg-primary-300',
    props.variant === 'outlined' &&
      'bg-white border border-gray100 disabled:bg-gray50',
    props.variant === 'danger' && 'bg-destructive disabled:bg-destructive-200',
    props.variant === 'transparent' && 'bg-transparent',
    className,
  );

  const typographyClassName = twMerge(
    props.variant === 'primary' && 'text-white',
    props.variant === 'outlined' && 'text-gray500',
    props.variant === 'danger' && 'text-white',
    props.variant === 'transparent' && 'text-gray500',
    props.leftIconProps && 'pl-2',
    props.rightIconProps && 'pr-2',
    labelClassName,
  );

  const loadingSpinnerClassName = twMerge(
    'animate-spin h-5 w-5',
    props.variant === 'primary' && 'text-white',
    props.variant === 'outlined' && 'text-gray500',
    props.variant === 'danger' && 'text-white',
    props.variant === 'transparent' && 'text-white',
  );

  return (
    <button
      className={buttonClassName}
      onClick={onClick}
      type="button"
      {...other}
    >
      {loading && (
        <svg
          className={loadingSpinnerClassName}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {leftIconProps && !loading && <Icon {...leftIconProps} />}
      <Typography
        variant="body-large-bold"
        as="p"
        className={typographyClassName}
      >
        {label}
      </Typography>
      {rightIconProps && <Icon {...rightIconProps} />}
    </button>
  );
};

export default Button;
